import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import { API_BASE_URL } from '../../config/api';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isDarkMode } = useTheme();
    
    // Pre-fill email from URL params if available
    const prefillEmail = searchParams.get('email') || '';

    const [formData, setFormData] = useState({
        email: prefillEmail,
        code: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(''); // Clear error when user starts typing
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Check if email is available
        if (!formData.email) {
            setError('Email is required. Please request a reset code first.');
            return;
        }

        // Validation
        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (formData.code.length !== 6) {
            setError('Reset code must be 6 digits');
            return;
        }

        try {
            setIsLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    code: formData.code,
                    new_password: formData.newPassword
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                // Redirect to sign in after 3 seconds
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            } else {
                setError(data.detail || 'Failed to reset password');
            }
        } catch (error) {
            setError('An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`reset-password-page ${isDarkMode ? 'dark-mode' : ''}`}>
            <NavBar />
            <div className="reset-password-container">
                <div className="reset-password-form-container">
                    <div className="reset-password-header">
                        <h1>Reset Password</h1>
                        <p>Enter the 6-digit code sent to your email and create a new password.</p>
                        {prefillEmail && (
                            <p className="email-info">Code sent to: <strong>{prefillEmail}</strong></p>
                        )}
                    </div>

                    <div className="reset-password-content">
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}

                        {success ? (
                            <div className="success-message">
                                <p>Password has been reset successfully!</p>
                                <p>Redirecting to sign in...</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="reset-password-form">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Reset Code (6 digits)"
                                        maxLength={6}
                                        pattern="[0-9]{6}"
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className="form-group password-input-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="newPassword"
                                        value={formData.newPassword}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="New Password (6-50 characters)"
                                        minLength={6}
                                        maxLength={50}
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        )}
                                    </button>
                                </div>

                                <div className="form-group password-input-wrapper">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Confirm New Password"
                                        minLength={6}
                                        maxLength={50}
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                    >
                                        {showConfirmPassword ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        )}
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    className="reset-password-submit-btn"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Resetting Password...' : 'Reset Password'}
                                </button>
                            </form>
                        )}

                        <div className="reset-password-footer">
                            <p>
                                Don't have a reset code?{' '}
                                <Link to="/forgot" className="forgot-link">
                                    Request one
                                </Link>
                            </p>
                            <p>
                                Remember your password?{' '}
                                <Link to="/" className="signin-link">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ResetPassword;

